/* eslint-disable no-shadow */
import React, { ComponentType, useState } from 'react'
import dynamic from 'next/dynamic'
import { getAnalyticsCountryCode } from '@helpers/env'
import { analytics } from '@helpers/analytics'
import FitOption from '@components/ui/FitOption'
import { selectedFitEvent } from '@helpers/products'
import { FINISHED_TO_ORDER, BUILD, ADDITIONAL_PROPERTIES } from '@consts/index'
import { checkIfLowerLegRulesApply } from '@helpers/filters'
import { BuildOptionWithHelperProps } from '@components/product/Filters/VariantFilter/BuildOptionWithHelper'
import { LowerLegOptionProps } from '@components/product/Filters/VariantFilter/LowerLegOption'
import styles from './styles.module.scss'
import { ProductOption } from 'types/product'
import { FilterOption } from 'types/filters'

const BuildOptionWithHelper: ComponentType<BuildOptionWithHelperProps> =
  dynamic(() => import('./BuildOptionWithHelper'))
const SizeHelper = dynamic(() => import('./SizeHelper'))
const LowerLegOption: ComponentType<LowerLegOptionProps> = dynamic(
  () => import('./LowerLegOption')
)
const SubTitle = dynamic(() => import('./Subtitle'))

const GRID_OPTION_TYPES = ['waist', 'leg', 'size', 'chest size']

const componentMap = {
  taper: LowerLegOption,
  build: BuildOptionWithHelper
} as const

function getIsOptionActive(
  optionsList: Omit<ProductOption, 'position' | 'name'>[],
  optionValue: string,
  type: string
) {
  const option = optionsList.find(option => option.type === type)

  if (!option) {
    return false
  }

  return (
    option?.values.findIndex(
      value =>
        value.toLowerCase() === optionValue.toLowerCase() ||
        value === FINISHED_TO_ORDER
    ) > -1
  )
}

type GetHasOptionValueProperties = {
  selectedOptions: FilterOption[]
  optionType: string
  optionValue: string
}

function getHasOptionValue({
  selectedOptions,
  optionType,
  optionValue
}: GetHasOptionValueProperties) {
  return selectedOptions.find(
    option => option.type === optionType && option.value === optionValue
  )
}

function getShouldShowFilterOption(
  type: string,
  selectedOptions: FilterOption[]
) {
  const isSelectedBuildX = getHasOptionValue({
    selectedOptions,
    optionType: 'build',
    optionValue: 'X'
  })

  if (isSelectedBuildX && type === BUILD.toLowerCase()) {
    return false
  }

  return true
}

type FilterType = Exclude<
  keyof typeof ADDITIONAL_PROPERTIES,
  'ignoreSizeOptionType'
>

type VariantFilterProps = {
  name: string
  type: string
  optionStyle?: string
  open?: boolean
  optionsList: Omit<ProductOption, 'position' | 'name'>[]
  position: number
  values: string[]
  variantExist: boolean
  index: number
  isVariant: boolean
  productType: string
  selectedOptions: FilterOption[]
  selectFilter: (filter: FilterOption[]) => void
}

const VariantFilter = ({
  name,
  type,
  index,
  isVariant,
  open,
  optionsList,
  position,
  values,
  variantExist,
  productType,
  selectedOptions,
  selectFilter
}: VariantFilterProps) => {
  const [helperOpen, setHelperOpen] = useState<string | null>(null)
  const shouldShowFilterOption = getShouldShowFilterOption(
    type,
    selectedOptions
  )

  const SizeFilterComponent =
    componentMap[(ADDITIONAL_PROPERTIES[type as FilterType] || {}).type] ||
    FitOption

  const helperPressed = (name: string) => {
    setHelperOpen(previousHelperOpen => (!previousHelperOpen ? name : null))
    // Helper Icon event
    analytics('Click', {
      category: 'Product Page',
      label: `${type.replace(/^\w/, c => c.toUpperCase())} help - ${
        helperOpen ? 'close' : 'open'
      }`,
      store_location: getAnalyticsCountryCode(),
      fulfillment: 'Normal',
      url: window.location.href
    })
  }

  const handleClickOutside = () => {
    setHelperOpen(null)
  }

  return (
    <div
      id={`filter-fit-option-${type}`}
      data-testid={`filter-fit-option-${type}`}
      className={`
       ${
         helperOpen
           ? styles.productForm__helper_wrapper
           : styles.productForm__section
       } ${open ? styles.open : ''}
      `}
    >
      <SubTitle
        showHelpButton={ADDITIONAL_PROPERTIES[type as FilterType]?.helperButton}
        helperOpen={!!helperOpen}
        helperPressed={helperPressed}
        name={isVariant ? `${index}. ${name}` : name}
        type={type}
        productType={productType}
        values={values}
      />
      <div
        className={`${styles.productForm__options} ${
          GRID_OPTION_TYPES.includes(type) ? styles.isGrid : ''
        } ${!shouldShowFilterOption ? styles.isHidden : ''}`}
      >
        {values.map((value, i) => {
          const isActive =
            !variantExist || getIsOptionActive(optionsList, value, type)
          const isSelected =
            selectedOptions.findIndex(option => {
              if (
                checkIfLowerLegRulesApply(selectedOptions) &&
                option.type === 'taper' &&
                option.value === 'Skinny'
              ) {
                return false
              }
              return (
                option.type === type &&
                option.value.toLowerCase() === value.toLowerCase()
              )
            }) > -1 && variantExist

          return (
            <SizeFilterComponent
              classes={type}
              helperOpen={helperOpen === type}
              optionValue={value}
              type={type}
              key={`${name}_${i.toString()}`}
              handleClickOutside={handleClickOutside}
              outsideClickIgnoreClass="productForm__helper__button"
              onSelect={() => {
                selectFilter([
                  {
                    position,
                    value,
                    type
                  }
                ])
                selectedFitEvent(name, value, {
                  product_type: productType
                })
              }}
              selected={isSelected}
              active={isActive}
            />
          )
        })}
      </div>
      <SizeHelper
        type={type}
        selectedOptions={selectedOptions}
        values={values}
        productType={productType}
      />
    </div>
  )
}

export default VariantFilter
